// src/components/pages/watch/SeriesPage.js
import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Box,
  Button,
  Card,
  CardContent,
  CardMedia,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  Divider,
  Grid,
  IconButton,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import PlayArrowRoundedIcon from "@mui/icons-material/PlayArrowRounded";
import { alpha } from "@mui/material/styles";
import { collection, doc, getDoc, getDocs, orderBy, query } from "firebase/firestore";
import { db } from "../../../firebase";
import { buildPosterImageAttrs } from "../../../utils/imageUrl";
import { getMainScrollContainer } from "../../../utils/layoutShell";
import { useSeoRoute } from "../../../seo";
import { APP_SHELL_LAYOUT } from "../../../constants/constants";

const PAGE_TOP_PADDING = 4;
// The bottom nav is fixed and sits on top of the page, so the last row of episode
// cards ends up underneath it on a long series. Clear the nav's full height (plus the
// device's bottom safe area) and keep the 32px of breathing room the page had before.
// Same treatment HistoryPage uses for its long list.
const PAGE_BOTTOM_PADDING_CSS = `calc(${APP_SHELL_LAYOUT.BOTTOM_NAV_OFFSET_CSS} + 32px)`;
const SECTION_SCROLL_MARGIN_TOP = 72;
const HEADER_RAIL_MIN_HEIGHT = 58;

function SectionHeader({ title, subtitle, trailing }) {
  return (
    <Box
      sx={{
        minHeight: HEADER_RAIL_MIN_HEIGHT,
        display: "flex",
        justifyContent: "space-between",
        alignItems: "flex-start",
        gap: 2,
      }}
    >
      <Box sx={{ minWidth: 0 }}>
        <Typography variant="h6" sx={{ fontWeight: 800, lineHeight: 1.12 }}>
          {title}
        </Typography>
        {subtitle ? (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.3 }}>
            {subtitle}
          </Typography>
        ) : null}
      </Box>
      {trailing ? <Box sx={{ flexShrink: 0, pt: 0.2 }}>{trailing}</Box> : null}
    </Box>
  );
}

function SeasonPills({ seasons, activeSeasonId, onSelect }) {
  if (seasons.length <= 1) return null;

  return (
    <Box
      sx={(theme) => ({
        position: "sticky",
        top: 0,
        zIndex: 2,
        py: 0.8,
        px: 0.9,
        mb: 1.4,
        borderRadius: 1.2,
        border: "1px solid",
        borderColor: "divider",
        backgroundColor:
          theme.palette.mode === "dark"
            ? alpha(theme.palette.background.paper, 0.92)
            : alpha(theme.palette.background.paper, 0.98),
        backdropFilter: "blur(8px)",
      })}
    >
      <Box
        sx={{
          display: "flex",
          gap: 0.8,
          overflowX: "auto",
          scrollbarWidth: "none",
          "&::-webkit-scrollbar": { display: "none" },
        }}
      >
        {seasons.map((season) => {
          const selected = season.id === activeSeasonId;
          return (
            <Box
              key={season.id}
              component="button"
              type="button"
              onClick={() => onSelect(season.id)}
              sx={(theme) => ({
                appearance: "none",
                border: "1px solid",
                borderColor: selected
                  ? theme.colors.blue
                  : theme.palette.mode === "dark"
                    ? alpha(theme.palette.text.secondary, 0.38)
                    : alpha(theme.colors.blue, 0.28),
                backgroundColor: selected
                  ? theme.colors.blue
                  : theme.palette.mode === "dark"
                    ? alpha(theme.palette.text.primary, 0.07)
                    : alpha(theme.colors.blue, 0.05),
                color: selected
                  ? theme.palette.common.white
                  : theme.palette.mode === "dark"
                    ? theme.palette.text.primary
                    : theme.colors.blue,
                borderRadius: "999px",
                px: 1.4,
                py: 0.55,
                minHeight: 38,
                whiteSpace: "nowrap",
                cursor: "pointer",
                fontSize: "0.9rem",
                fontWeight: 800,
                flex: "0 0 auto",
              })}
            >
              {season.title || `Season ${season.number}`}
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}

function EpisodeCard({ episode, fallbackPosterUrl, onPlay, onInfo }) {
  const posterAttrs = buildPosterImageAttrs(episode.posterUrl || fallbackPosterUrl, {
    sizes: "(max-width: 600px) 50vw, (max-width: 900px) 33vw, 25vw",
  });

  return (
    <Card elevation={0} sx={{ borderRadius: 1.4, backgroundColor: "transparent" }}>
      <Box
        onClick={onPlay}
        aria-label={`Play Episode ${episode.episodeNumber}: ${episode.title}`}
        sx={{
          position: "relative",
          cursor: "pointer",
          borderRadius: 1.4,
          overflow: "hidden",
          boxShadow: (theme) =>
            theme.palette.mode === "dark"
              ? "0 10px 24px rgba(0,0,0,0.30)"
              : "0 10px 24px rgba(15,42,115,0.16)",
        }}
      >
        <CardMedia
          component="img"
          image={posterAttrs.src}
          srcSet={posterAttrs.srcSet || undefined}
          sizes={posterAttrs.sizes || undefined}
          loading="lazy"
          decoding="async"
          alt={episode.title}
          sx={{ width: "100%", aspectRatio: "16/9", objectFit: "cover" }}
        />

        <Box
          sx={{
            position: "absolute",
            inset: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            pointerEvents: "none",
            background: "linear-gradient(180deg, rgba(0,0,0,0.02) 0%, rgba(0,0,0,0.35) 100%)",
          }}
        >
          <Box
            sx={{
              width: 44,
              height: 44,
              borderRadius: "50%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              bgcolor: (theme) => alpha(theme.palette.common.black, 0.35),
            }}
          >
            <PlayArrowRoundedIcon sx={{ color: "common.white", fontSize: 28 }} />
          </Box>
        </Box>

        <IconButton
          onClick={(event) => {
            event.stopPropagation();
            onInfo();
          }}
          aria-label={`Learn more about Episode ${episode.episodeNumber}`}
          sx={{
            position: "absolute",
            top: 6,
            right: 6,
            bgcolor: (theme) => alpha(theme.palette.common.black, 0.55),
            color: "common.white",
            "&:hover": { bgcolor: (theme) => alpha(theme.palette.common.black, 0.7) },
          }}
        >
          <InfoOutlinedIcon fontSize="small" />
        </IconButton>
      </Box>

      <CardContent sx={{ p: 0, mt: 1 }}>
        <Typography variant="subtitle2" fontWeight="bold" noWrap>
          Episode {episode.episodeNumber}: {episode.title}
        </Typography>
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{
            overflow: "hidden",
            textOverflow: "ellipsis",
            display: "-webkit-box",
            WebkitLineClamp: 2,
            WebkitBoxOrient: "vertical",
          }}
        >
          {episode.description || "No description available."}
        </Typography>
      </CardContent>
    </Card>
  );
}

function LoadingState() {
  return (
    <Box
      sx={{
        minHeight: 320,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        textAlign: "center",
        gap: 1,
      }}
    >
      <CircularProgress />
      <Typography variant="body2" color="text.secondary">
        Loading series details...
      </Typography>
    </Box>
  );
}

function SeriesNotFoundState() {
  return (
    <Box
      sx={{
        minHeight: "48vh",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        textAlign: "center",
        gap: 1,
      }}
    >
      <Typography variant="h6" sx={{ fontWeight: 700 }}>
        Series not found
      </Typography>
      <Typography variant="body2" color="text.secondary">
        This series may have been removed or is unavailable.
      </Typography>
    </Box>
  );
}

const SeriesPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [pageState, setPageState] = useState({ status: "loading", series: null, seasons: [] });
  const [activeSeasonId, setActiveSeasonId] = useState(null);
  const [modalEpisode, setModalEpisode] = useState(null);
  const [seriesInfoOpen, setSeriesInfoOpen] = useState(false);

  useSeoRoute(
    pageState.series
      ? {
          id: pageState.series.id || id,
          title: pageState.series.title || pageState.series.name,
          description: pageState.series.description || "",
          image: pageState.series.posterUrl || pageState.series.coverUrl || "",
        }
      : { id }
  );

  const seasonRefs = useRef({});

  useLayoutEffect(() => {
    const mainScrollContainer = getMainScrollContainer();
    if (mainScrollContainer) {
      mainScrollContainer.scrollTop = 0;
      mainScrollContainer.scrollLeft = 0;
    }
    if (typeof window !== "undefined") {
      window.scrollTo({ top: 0, left: 0, behavior: "auto" });
    }
  }, [id]);

  useEffect(() => {
    let cancelled = false;

    seasonRefs.current = {};
    setPageState({ status: "loading", series: null, seasons: [] });
    setActiveSeasonId(null);

    (async () => {
      try {
        const seriesSnap = await getDoc(doc(db, "series", id));
        if (!seriesSnap.exists()) {
          if (!cancelled) setPageState({ status: "not_found", series: null, seasons: [] });
          return;
        }

        const seriesData = { id: seriesSnap.id, ...seriesSnap.data() };
        const seasonSnaps = await getDocs(
          query(collection(db, "series", id, "seasons"), orderBy("number", "asc"))
        );

        const seasons = await Promise.all(
          seasonSnaps.docs.map(async (seasonDoc) => {
            const seasonData = { id: seasonDoc.id, ...seasonDoc.data() };
            const episodeSnaps = await getDocs(
              query(
                collection(db, "series", id, "seasons", seasonDoc.id, "episodes"),
                orderBy("episodeNumber", "asc")
              )
            );

            const episodes = episodeSnaps.docs
              .map((episodeDoc) => ({ id: episodeDoc.id, ...episodeDoc.data() }))
              .sort((a, b) => (a.episodeNumber ?? 0) - (b.episodeNumber ?? 0));

            return { ...seasonData, episodes };
          })
        );

        if (cancelled) return;

        setPageState({ status: "ready", series: seriesData, seasons });
        setActiveSeasonId(seasons[0]?.id || null);
      } catch (error) {
        console.error(error);
        if (!cancelled) {
          setPageState({ status: "not_found", series: null, seasons: [] });
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [id]);

  const { status, series, seasons } = pageState;

  const totalEpisodes = useMemo(
    () => seasons.reduce((count, season) => count + (season.episodes?.length || 0), 0),
    [seasons]
  );

  const firstEpisode = useMemo(() => {
    for (const season of seasons) {
      if (season.episodes?.length) return season.episodes[0];
    }
    return null;
  }, [seasons]);

  const seriesHeroPosterAttrs = buildPosterImageAttrs(series?.landscapePosterUrl, {
    sizes: "(max-width: 900px) 96vw, 900px",
  });

  const playEpisode = (episode) => {
    navigate(`/video/${episode.id}`, {
      state: { videoMeta: episode, contentType: "episode", autoPlay: true },
    });
  };

  const handleSeasonSelect = (seasonId) => {
    setActiveSeasonId(seasonId);
    const node = seasonRefs.current[seasonId];
    if (!node) return;
    node.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const renderMainContent = () => {
    if (status === "loading") return <LoadingState />;
    if (status === "not_found" || !series) return <SeriesNotFoundState />;

    return (
      <>
        <SectionHeader
          title={series.title}
          subtitle="Episode guide and season collections"
          trailing={
            <Chip
              label={`${totalEpisodes} episodes`}
              size="small"
              sx={(theme) => ({
                fontWeight: 700,
                backgroundColor:
                  theme.palette.mode === "dark"
                    ? alpha(theme.palette.text.primary, 0.08)
                    : alpha(theme.colors.blue, 0.08),
              })}
            />
          }
        />

        <Card
          elevation={0}
          sx={(theme) => ({
            borderRadius: 2,
            overflow: "hidden",
            mb: 2.2,
            background:
              theme.palette.mode === "dark"
                ? "linear-gradient(180deg, rgba(255,255,255,0.02), rgba(255,255,255,0.01))"
                : "linear-gradient(180deg, rgba(35,65,161,0.04), rgba(35,65,161,0.015))",
            border: "1px solid",
            borderColor: "divider",
          })}
        >
          <CardMedia
            component="img"
            image={seriesHeroPosterAttrs.src}
            srcSet={seriesHeroPosterAttrs.srcSet || undefined}
            sizes={seriesHeroPosterAttrs.sizes || undefined}
            loading="eager"
            fetchPriority="high"
            decoding="async"
            alt={series.title}
            sx={{ width: "100%", aspectRatio: "16/9", objectFit: "cover" }}
          />

          <CardContent sx={{ p: 2 }}>
            {series.description ? (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  display: "-webkit-box",
                  WebkitLineClamp: 3,
                  WebkitBoxOrient: "vertical",
                  mb: 1.4,
                }}
              >
                {series.description}
              </Typography>
            ) : null}

            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
              <Button
                variant="contained"
                onClick={() => firstEpisode && playEpisode(firstEpisode)}
                disabled={!firstEpisode}
                sx={(theme) => ({
                  backgroundColor: theme.colors?.blue || undefined,
                  color: theme.colors?.white || undefined,
                  fontWeight: 700,
                })}
              >
                {firstEpisode ? "Play from start" : "No episodes yet"}
              </Button>

              <Button variant="outlined" onClick={() => setSeriesInfoOpen(true)} sx={{ fontWeight: 700 }}>
                Series details
              </Button>
            </Box>
          </CardContent>
        </Card>

        <SeasonPills seasons={seasons} activeSeasonId={activeSeasonId} onSelect={handleSeasonSelect} />

        <Divider sx={{ mb: 1.7 }} />

        {seasons.length === 0 ? (
          <Box sx={{ py: 4, textAlign: "center" }}>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              No seasons published yet
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.6 }}>
              Check back soon for new episodes.
            </Typography>
          </Box>
        ) : (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2.2 }}>
            {seasons.map((season) => (
              <Box
                key={season.id}
                id={season.id}
                ref={(node) => {
                  seasonRefs.current[season.id] = node;
                }}
                sx={{ scrollMarginTop: `${SECTION_SCROLL_MARGIN_TOP}px` }}
              >
                <SectionHeader
                  title={season.title || `Season ${season.number}`}
                  subtitle={`${season.episodes?.length || 0} episodes`}
                />

                <Grid container spacing={2}>
                  {season.episodes.map((episode) => (
                    <Grid item xs={6} sm={6} md={4} lg={3} key={episode.id}>
                      <EpisodeCard
                        episode={episode}
                        fallbackPosterUrl={series.portraitPosterUrl}
                        onPlay={() => playEpisode(episode)}
                        onInfo={() => setModalEpisode(episode)}
                      />
                    </Grid>
                  ))}
                </Grid>
              </Box>
            ))}
          </Box>
        )}
      </>
    );
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        bgcolor: "background.default",
        color: "text.primary",
        overflowAnchor: "none",
      }}
    >
      <Box
        sx={(theme) => ({
          position: "relative",
          px: 2,
          pt: PAGE_TOP_PADDING,
          pb: PAGE_BOTTOM_PADDING_CSS,
          width: "100%",
          maxWidth: 920,
          mx: "auto",
          "&::before": {
            content: '""',
            position: "absolute",
            top: -70,
            left: -24,
            width: 220,
            height: 220,
            borderRadius: "50%",
            background:
              theme.palette.mode === "dark"
                ? "radial-gradient(circle, rgba(76,150,255,0.22) 0%, rgba(76,150,255,0) 72%)"
                : "radial-gradient(circle, rgba(76,150,255,0.18) 0%, rgba(76,150,255,0) 72%)",
            pointerEvents: "none",
          },
        })}
      >
        <Box sx={{ position: "relative", zIndex: 1 }}>{renderMainContent()}</Box>
      </Box>

      <Dialog
        open={Boolean(modalEpisode)}
        onClose={() => setModalEpisode(null)}
        maxWidth="md"
        fullWidth
        sx={{
          "& .MuiDialog-paper": {
            bgcolor: "background.paper",
            border: "1px solid",
            borderColor: "divider",
            borderRadius: 2,
          },
        }}
      >
        {modalEpisode && (
          <DialogContent sx={{ p: 0 }}>
            <Box sx={{ position: "relative" }}>
              <IconButton
                onClick={() => setModalEpisode(null)}
                aria-label="Close details"
                sx={{
                  position: "absolute",
                  top: 8,
                  right: 8,
                  bgcolor: (theme) => alpha(theme.palette.common.black, 0.5),
                  color: "common.white",
                  "&:hover": { bgcolor: (theme) => alpha(theme.palette.common.black, 0.7) },
                }}
              >
                <CloseIcon />
              </IconButton>
            </Box>

            <Box sx={{ p: 3 }}>
              <Typography variant="h6" fontWeight="bold" gutterBottom>
                Episode {modalEpisode.episodeNumber}: {modalEpisode.title}
              </Typography>

              <Typography variant="body1" paragraph>
                {modalEpisode.description}
              </Typography>

              <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", mb: 2 }}>
                {modalEpisode.mpaRating && (
                  <Typography>
                    <strong>Rating:</strong> {modalEpisode.mpaRating}
                  </Typography>
                )}
                {modalEpisode.year && (
                  <Typography>
                    <strong>Year:</strong> {modalEpisode.year}
                  </Typography>
                )}
              </Box>

              {!!modalEpisode.category?.length && (
                <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 2 }}>
                  {modalEpisode.category.map((category) => (
                    <Chip key={category} label={category} />
                  ))}
                </Box>
              )}

              {!!modalEpisode.tags?.length && (
                <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 3 }}>
                  {modalEpisode.tags.map((tag) => (
                    <Chip key={tag} label={tag} />
                  ))}
                </Box>
              )}

              <Button
                variant="contained"
                fullWidth
                size="large"
                onClick={() => playEpisode(modalEpisode)}
                sx={(theme) => ({
                  backgroundColor: theme.colors?.blue || undefined,
                  color: theme.colors?.white || undefined,
                  p: "10px 20px",
                  fontWeight: 700,
                })}
              >
                Watch now
              </Button>
            </Box>
          </DialogContent>
        )}
      </Dialog>

      <Dialog
        open={seriesInfoOpen}
        onClose={() => setSeriesInfoOpen(false)}
        maxWidth="sm"
        fullWidth
        sx={{
          "& .MuiDialog-paper": {
            bgcolor: "background.paper",
            border: "1px solid",
            borderColor: "divider",
            borderRadius: 2,
          },
        }}
      >
        <DialogContent sx={{ p: 0 }}>
          <Box sx={{ position: "relative" }}>
            <IconButton
              onClick={() => setSeriesInfoOpen(false)}
              aria-label="Close details"
              sx={{
                position: "absolute",
                top: 8,
                right: 8,
                bgcolor: (theme) => alpha(theme.palette.common.black, 0.5),
                color: "common.white",
                "&:hover": { bgcolor: (theme) => alpha(theme.palette.common.black, 0.7) },
              }}
            >
              <CloseIcon />
            </IconButton>
          </Box>

          <Box sx={{ p: 3 }}>
            <Typography variant="h6" fontWeight="bold" gutterBottom>
              {series?.title}
            </Typography>
            {series?.description ? (
              <Typography variant="body1" paragraph>
                {series.description}
              </Typography>
            ) : (
              <Typography variant="body2" color="text.secondary">
                No series description available.
              </Typography>
            )}
          </Box>
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default SeriesPage;
